import { REJECT, array, bool, nullable, num, object, oneOf, optional, pureTransform, str, union, unknownRecord, unknownValue } from 'validation';

// RetentionPolicy / retentionPolicySchema are canonical in api/shared (the same
// JSONB array is carried by zones too); re-exported here so the node_definitions
// endpoints keep importing them from `./types`.
import { retentionPolicySchema, type RetentionPolicy } from 'api/shared/retentionPolicy';
export { retentionPolicySchema };
export type { RetentionPolicy };

// Shared sub-object types + schemas for the node_definitions endpoints, plus the
// POST/PUT request body.
//
// The sub-objects are shared by IDENTITY: the backend serializes them from one
// place — the JSON_AGG subqueries in
// `modules/generic_functions/node_definitions/getNodeDefinitionByZoneAndName.js`
// — reused across the record responses. The flat record ENVELOPE, by contrast,
// is assembled separately by each GET/POST/PUT route handler (equal-by-
// coincidence), so it is redeclared per endpoint; see ../../README.md.

export type AutoSubmitMode = 'if_none_pending' | 'always';

export const AUTO_SUBMIT_MODES: readonly AutoSubmitMode[] = ['if_none_pending', 'always'];

// One output matcher: an ordered rule that matches either a script node's output
// file content or its agent-reported return code. Fields mirror the backend
// table `node_definition_output_matchers` (and the GET JSON_BUILD_OBJECT). Only
// ever present on script-producing node types. `sort_order` is server-assigned
// (present on read, omitted on write — array position conveys order).
// `match_type` selects the mutually-exclusive match source (`file` default).
//
// Deploy order: this UI must not land ahead of the backend that persists
// `match_type` / `return_codes` (the `output_matcher_match_type_return_codes`
// migration — named, not numbered, because an unmerged migration gets renumbered
// whenever another branch claims its id first). A return_code save omits
// `search_string`; an older backend treats the payload as a file matcher and
// 400s. Ship BE (+ migration) before or in lockstep with FE — the general
// FE/BE version-skew policy is still open (see the follow-up issue).
export type OutputMatcherFile = 'stdout.log' | 'stderr.log';
export type OutputMatcherProceed = 'continue' | 'abort';
export type OutputMatcherMatchType = 'file' | 'return_code';

const OUTPUT_MATCHER_FILES: readonly OutputMatcherFile[] = ['stdout.log', 'stderr.log'];
const OUTPUT_MATCHER_PROCEED: readonly OutputMatcherProceed[] = ['continue', 'abort'];

// One node-definition parameter. Shared between the record responses and the Save body: the backend round-trips the same
// field names. Loose at the type level — the backend owns the inter-field rules. `name` / `parameter_type` are the only
// always-present, non-null fields; the rest are optional/defaulted on write and nullable on read. `options` is opaque JSON.
// `order` is server-assigned (present on read, optional on write; array position conveys it). A `default_expression` of
// '******' on read means the value is a masked Password; sending that literal back keeps the stored secret (backend convention).
export interface Parameter {
  // Both are nullable columns on `node_definition_parameters`, and unlike the definition's own `name` the backend does
  // not validate them on write, so a null is reachable through the API rather than only by direct SQL.
  readonly name: string | null;
  readonly parameter_type: string | null;
  readonly default_expression?: string | null;
  readonly runtime?: boolean;
  readonly inout?: string;
  readonly options?: unknown;
  readonly mandatory?: boolean;
  readonly output_format?: string | null;
  readonly order?: number;
  readonly description?: string | null;
}

// A wait-event link at the DEFINITION level (full shape). Note the same key `wait_events` also appears per child-node
// placement with a NARROWER shape (only auto_submit/auto_clear) — that one lives inside the opaque `child_nodes` blob and is
// not this type. `path` / `scheduling_window` are "zone|name" strings; the backend resolves them.
export interface WaitEvent {
  readonly path: string | null;
  readonly auto_submit?: boolean;
  // Only meaningful when auto_submit is true; the backend rejects a mode without auto_submit.
  readonly auto_submit_mode?: AutoSubmitMode | null;
  // IANA timezone the auto-submit runs in; null falls back to the configured default, then UTC. Like the mode, the backend
  // rejects a value without auto_submit.
  readonly auto_submit_timezone?: string | null;
  readonly auto_clear?: boolean;
  readonly scheduling_window?: string | null;
  readonly precondition?: string | null;
}

// A raise-event link at the definition level. `path` is a "zone|name" string.
export interface RaiseEvent {
  readonly path: string | null;
  readonly raiser_comment?: string | null;
  readonly status?: string | null;
  readonly precondition?: string | null;
}

// The Handle half, shared by both branches. These are real shared columns on the one
// `node_definition_output_matchers` table, so extending is the true relationship — not the
// banned "one endpoint's envelope derived from another's" (see ../../README.md).
interface OutputMatcherHandle {
  // Overrides the agent-reported exit code; the derived status follows (0 -> completed, non-zero -> failed).
  readonly new_exit_code?: number | null;
  readonly proceed?: OutputMatcherProceed;
  // Server-assigned; present on read, omitted on write (order conveyed by array position).
  readonly sort_order?: number;
}

// Matches when `search_string` is found in `output_file`.
export interface OutputMatcherFileMatch extends OutputMatcherHandle {
  readonly match_type: 'file';
  readonly output_file?: OutputMatcherFile | null;
  readonly search_string?: string | null;
  readonly case_insensitive?: boolean;
  // Treat search_string as a Go RE2 regex (validated at save time on the backend).
  readonly use_regex?: boolean;
}

// Matches when the agent-reported exit code is one of `return_codes` (set membership).
export interface OutputMatcherReturnCodeMatch extends OutputMatcherHandle {
  readonly match_type: 'return_code';
  readonly return_codes: readonly number[];
}

// The match source is mutually exclusive, and the union says so: neither branch can name the
// other's fields, so a projection cannot accidentally send `search_string` with a return_code
// matcher. `chk_node_definition_output_matchers_match_source` enforces the same XOR in the DB.
export type OutputMatcher = OutputMatcherFileMatch | OutputMatcherReturnCodeMatch;

export const parameterSchema = object<Parameter>({
  name: nullable(str()),
  parameter_type: nullable(str()),
  default_expression: optional(nullable(str())),
  runtime: optional(bool()),
  inout: optional(str()),
  options: optional(unknownValue()),
  mandatory: optional(bool()),
  output_format: optional(nullable(str())),
  order: optional(num()),
  description: optional(nullable(str())),
});

export const waitEventSchema = object<WaitEvent>({
  path: nullable(str()),
  auto_submit: optional(bool()),
  auto_submit_mode: optional(nullable(oneOf(AUTO_SUBMIT_MODES, 'if_none_pending'))),
  auto_submit_timezone: optional(nullable(str())),
  auto_clear: optional(bool()),
  scheduling_window: optional(nullable(str())),
  precondition: optional(nullable(str())),
});

export const raiseEventSchema = object<RaiseEvent>({
  path: nullable(str()),
  raiser_comment: optional(nullable(str())),
  status: optional(nullable(str())),
  precondition: optional(nullable(str())),
});

/** Shared handle fields on both match_type branches. */
const outputMatcherHandleShape = {
  new_exit_code: optional(nullable(num())),
  proceed: optional(oneOf(OUTPUT_MATCHER_PROCEED, 'continue')),
  sort_order: optional(num()),
};

/**
 * Discriminated on `match_type` (`NOT NULL DEFAULT 'file'` on the table, so always on the wire). Each branch parses only
 * its own source fields; the other branch's are ignored as extras. A row whose `match_type` the frontend does not know is
 * dropped by `union` — see the FE/BE version-skew issue for the policy that should replace dropping.
 */
export const outputMatcherSchema = union<OutputMatcher, 'match_type'>('match_type', {
  file: {
    output_file: optional(nullable(oneOf(OUTPUT_MATCHER_FILES, 'stdout.log'))),
    search_string: optional(nullable(str())),
    case_insensitive: optional(bool()),
    use_regex: optional(bool()),
    ...outputMatcherHandleShape,
  },
  return_code: {
    // Not checked for non-emptiness: the type cannot express it, and failing the row here would delete a matcher on the
    // next save. The DB CHECK guarantees a non-empty set on read; on write the backend 400s an empty one.
    return_codes: array(num()),
    ...outputMatcherHandleShape,
  },
});

/**
 * A node output handle (`node_definitions.handles` JSONB). The backend checks `id` (a UUID) and `label` (a non-empty string)
 * on write, but not the other fields' types: `condition` is an expression on `conditions` nodes and `return_codes` a
 * comma-separated string on every other type, never both, and neither is checked (nap-backend #1963). `id` is the stable
 * handle identity the Flow graph's edges reference.
 */
export interface Handle {
  readonly id: string;
  readonly label: string;
  readonly condition?: string;
  readonly return_codes?: string;
}

export const handleSchema = object<Handle>({
  id: str(REJECT),
  label: str(),
  condition: optional(str()),
  return_codes: optional(str()),
});

// The Flow graph of a `flow` definition: its placements (`flow_definition_nodes`) and edges (`flow_definition_edges`). One
// serializer produces both — the child_nodes / child_edges JSON_BUILD_OBJECTs in
// `modules/generic_functions/node_definitions/getNodeDefinitionByZoneAndName.js` — so these are shared by IDENTITY between
// every contract over that query. Today only the projection `getNodeDefinitionGraph` declares them; `NodeDefinition` still
// holds the graph opaque, because typing an EDITOR's graph is blocked on #1282 (`object()` strips undeclared keys).
//
// The per-placement JSONB members are front-end conventions the backend stores largely as sent; each type notes what the
// backend does check (nap-backend `insertFlowDefinitionGraph.js` and the validators it calls).

/**
 * One parameter override on a placement (`flow_definition_nodes.parameters`, checked only to be an array). A key's ABSENCE is
 * meaningful — "inherit from the definition" — so the fields are optional rather than nullable. `runtime` may arrive as the
 * string 'true' from older data.
 */
export type FlowParameterOverride = {
  readonly name: string | null;
  readonly value?: string;
  readonly runtime?: boolean | string;
  readonly parent_parameter_out?: string;
};

/** A wait-event link on a placement. `path` is "zone|name"; `auto_submit` / `auto_clear` are checked as booleans and defaulted. */
export interface FlowNodeWaitEvent {
  readonly path: string | null;
  readonly auto_submit?: boolean;
  readonly auto_clear?: boolean;
  readonly precondition?: string | null;
}

/** A raise-event link on a placement. `path` is "zone|name"; the other fields are checked as strings and blanked to null. */
export interface FlowNodeRaiseEvent {
  readonly path: string | null;
  readonly raiser_comment?: string | null;
  readonly status?: string | null;
  readonly precondition?: string | null;
}

export type FlowHandleStatusAction = 'raise_operator_message' | 'fail_flow' | 'complete_flow';

const FLOW_HANDLE_STATUS_ACTIONS: readonly FlowHandleStatusAction[] = ['raise_operator_message', 'fail_flow', 'complete_flow'];

/** One placement (`flow_definition_nodes` row, with its child definition's columns joined in). */
export interface FlowDefinitionNode {
  // `flow_node_id`: the canvas node id, which edges reference by `source` / `target`.
  readonly id: string;
  // Every child-definition column is LEFT JOINed, so all of them are nullable, as is the placement's own reference.
  readonly node_definition_id: number | null;
  readonly node_definition_name: string | null;
  readonly node_definition_path: string | null;
  readonly zone_name: string | null;
  readonly description: string | null;
  readonly type: string | null;
  readonly handles: readonly Handle[] | null;
  // Placement overrides; null means "use the definition's".
  readonly label: string | null;
  readonly flow_description: string | null;
  readonly parameters: readonly FlowParameterOverride[] | null;
  readonly precondition: string | null;
  // Nullable columns, defaulting to 0.
  readonly position: { readonly x: number | null; readonly y: number | null };
  readonly queue_path: string | null;
  readonly precondition_scheduling_window: string | null;
  readonly precondition_scheduling_window_status: string | null;
  readonly schedule_scheduling_window: string | null;
  readonly schedule_scheduling_window_status: string | null;
  // "HH:MM" (the column is text since migration 0248; older data may carry seconds).
  readonly schedule_start_time: string | null;
  // Keyed by handle id (or a lifecycle status). The backend accepts any value; these three are what the frontend writes.
  readonly handle_status_actions: Readonly<Record<string, FlowHandleStatusAction>> | null;
  readonly wait_events: readonly FlowNodeWaitEvent[] | null;
  readonly raise_events: readonly FlowNodeRaiseEvent[] | null;
  // "zone|lock" tags.
  readonly locks: readonly string[] | null;
  readonly submit_disabled: boolean;
  readonly submit_and_forget: boolean;
  readonly node_data: unknown;
}

/** One edge (`flow_definition_edges` row). */
export interface FlowDefinitionEdge {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly type: string;
  readonly sourceHandle: string;
  readonly targetHandle: string | null;
  // Routing waypoints and, on loop edges, the restart limits. May arrive as a JSON string; see `parseEdgeDataObject`.
  readonly data: unknown;
}

const flowParameterOverrideSchema = object<FlowParameterOverride>({
  name: nullable(str()),
  value: optional(str()),
  runtime: optional(pureTransform(unknownValue(), (v) => (typeof v === 'boolean' || typeof v === 'string' ? v : undefined))),
  parent_parameter_out: optional(str()),
});

const flowNodeWaitEventSchema = object<FlowNodeWaitEvent>({
  path: nullable(str()),
  auto_submit: optional(bool()),
  auto_clear: optional(bool()),
  precondition: optional(nullable(str())),
});

const flowNodeRaiseEventSchema = object<FlowNodeRaiseEvent>({
  path: nullable(str()),
  raiser_comment: optional(nullable(str())),
  status: optional(nullable(str())),
  precondition: optional(nullable(str())),
});

const isFlowHandleStatusAction = (v: unknown): v is FlowHandleStatusAction => (FLOW_HANDLE_STATUS_ACTIONS as readonly unknown[]).includes(v);

// All or nothing: a map holding a value the frontend does not know is unreadable as a whole, and `nullable` reports it as
// substituted by null, rather than a silently shorter map.
const handleStatusActionsSchema = pureTransform(unknownRecord(), (map) =>
  Object.values(map).every(isFlowHandleStatusAction) ? (map as Record<string, FlowHandleStatusAction>) : undefined
);

export const flowDefinitionNodeSchema = object<FlowDefinitionNode>({
  id: str(REJECT),
  node_definition_id: nullable(num()),
  node_definition_name: nullable(str()),
  node_definition_path: nullable(str()),
  zone_name: nullable(str()),
  description: nullable(str()),
  type: nullable(str()),
  handles: nullable(array(handleSchema)),
  label: nullable(str()),
  flow_description: nullable(str()),
  parameters: nullable(array(flowParameterOverrideSchema)),
  precondition: nullable(str()),
  position: object<FlowDefinitionNode['position']>({ x: nullable(num()), y: nullable(num()) }),
  queue_path: nullable(str()),
  precondition_scheduling_window: nullable(str()),
  precondition_scheduling_window_status: nullable(str()),
  schedule_scheduling_window: nullable(str()),
  schedule_scheduling_window_status: nullable(str()),
  schedule_start_time: nullable(str()),
  handle_status_actions: nullable(handleStatusActionsSchema),
  wait_events: nullable(array(flowNodeWaitEventSchema)),
  raise_events: nullable(array(flowNodeRaiseEventSchema)),
  locks: nullable(array(str())),
  submit_disabled: bool(),
  submit_and_forget: bool(),
  node_data: unknownValue(),
});

export const flowDefinitionEdgeSchema = object<FlowDefinitionEdge>({
  id: str(REJECT),
  source: str(REJECT),
  target: str(REJECT),
  type: str(),
  sourceHandle: str(),
  targetHandle: nullable(str()),
  data: unknownValue(),
});

// POST/PUT request body. Source of truth for what the backend accepts: nap-backend
//   routes/node_definitions.js POST / PUT handlers        — body destructure + folder/queue/scheduling-window/runtime-limit resolution.
//   modules/generic_functions/node_definitions/upsertNodeDefinition.js — the field reads and per-collection validation.
//
// Permissive on purpose: fields the backend requires are typed `T | null`, not strictly `T` — the server owns the validity
// rules and returns 400 on a bad body, which the UI surfaces. A too-strict client type would block valid sends whenever the
// backend's rules loosen. That is about VALUES, not structure: `output_matchers` is a discriminated union because its two
// match sources are mutually exclusive by construction and the frontend always satisfies that. Taken to its limit,
// permissiveness types the whole body `any` and buys nothing. The opaque members (`config`, `handles`, `child_nodes`, `child_edges`) are forwarded exactly as
// held; the strict collections reuse the wire sub-object types above (same field names round-trip). The FE authors this body,
// so it is never validated here.
export interface SaveNodeDefinition {
  readonly name: string | null;
  readonly description: string | null;
  readonly type: string | null;
  readonly sub_type: string | null;
  // The backend resolves `zone_name` -> zone_id (and PUT also infers it from the URL).
  readonly zone_name: string | null;
  // Folder PATH string (the backend resolves it to an id); `folder_id` is never sent.
  readonly folder_name: string | null;
  // "queueZone|queueName" string.
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
  // "zone|lock" tag strings.
  readonly locks: readonly string[];
  readonly retention_policies: readonly RetentionPolicy[];
  // Ordered; sending `[]` clears all matchers, omitting the key would leave stored matchers unchanged — the model always
  // sends the authoritative array.
  readonly output_matchers: readonly OutputMatcher[];
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  // Ordered exit codes to restrict auto-restart to (negatives allowed); empty restarts on any failure. The backend dedupes
  // preserving order and coerces integer-like strings, so the client sends what the user entered.
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  // HH:MM:SS (or a "{{var}}" / "={{var}}*1000" expression); the backend converts to the ms columns.
  readonly runtime_limit_min_hhmmss: string | null;
  readonly runtime_limit_max_hhmmss: string | null;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  readonly documentation: string | null;
  // Optimistic-concurrency token (PUT only): the loaded `version`. Omitted on create; the backend skips the check when absent.
  readonly if_match_version?: number;
  // PUT only: parameter names deleted in this save, so the backend strips their mappings from referencing flow nodes.
  readonly remove_parameter_names?: readonly string[];
}
